import React, { useEffect, useRef } from 'react'
import { imageUrl, imageSrcSet } from '../../utils/imageUrl'
import { getGeneralInfo } from '../../utils/generalInfo'
import { handleImageFallback } from '../../utils/brand'
import { useAppType } from '../../context/AppContext'
import { prefersReducedMotion } from '../../utils/fx'
import './InfoSection.scss'

const c = 'info-section'
const AUTOPLAY_MS = 4000

const InfoSection = () => {
  const appType = useAppType()
  const images: string[] = getGeneralInfo().images || []
  const trackRef = useRef<HTMLDivElement>(null)
  const paused = useRef(false)

  useEffect(() => {
    const first = images[0]
    if (!first) return
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = imageUrl(first, 800)
    link.setAttribute('imagesrcset', imageSrcSet(first, [480, 800, 1280]))
    link.setAttribute('imagesizes', '(max-width: 640px) 50vw, 25vw')
    link.setAttribute('fetchpriority', 'high')
    document.head.appendChild(link)
    return () => {
      document.head.removeChild(link)
    }
  }, [images[0]])

  useEffect(() => {
    const track = trackRef.current
    if (!track || images.length < 2 || prefersReducedMotion()) return

    const id = window.setInterval(() => {
      const overflow = track.scrollWidth - track.clientWidth
      if (paused.current || overflow < 8) return
      const card = track.firstElementChild as HTMLElement | null
      const step = (card?.offsetWidth ?? 0) + 12
      const atEnd = track.scrollLeft >= overflow - 8
      track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + step, behavior: 'smooth' })
    }, AUTOPLAY_MS)

    return () => window.clearInterval(id)
  }, [images.length])

  if (images.length === 0) {
    return null
  }

  const pause = () => {
    paused.current = true
  }
  const resume = () => {
    paused.current = false
  }

  return (
    <section className={c} data-count={images.length}>
      <div
        ref={trackRef}
        className={`${c}__track`}
        onTouchStart={pause}
        onTouchEnd={resume}
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        {images.map((image, index) => (
          <div
            key={image}
            className={`${c}__card`}
            style={{ animationDelay: `${index * 70}ms` }}
          >
            <img
              src={imageUrl(image, 800)}
              srcSet={imageSrcSet(image, [480, 800, 1280])}
              sizes='(max-width: 640px) 50vw, 25vw'
              alt={`banner-${index + 1}`}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding='async'
              fetchPriority={index === 0 ? 'high' : 'auto'}
              onError={handleImageFallback(appType)}
              draggable={false}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

export default InfoSection
